import type { GapTranslate } from "../findCableGaps";
import type {
  KarlWizardState,
  KarlWizardStep,
  KarlWizardWallCounts,
} from "./types";

// Totals, never what is left: the card stays in the thread and must read the same when he scrolls back.
// Found is what Karl located; working is his set after removing and adding boards.
const boardsQuestion = (
  t: GapTranslate,
  found: number,
  working?: { names: string[] },
): string =>
  working && working.names.length !== found
    ? t("smartSurveyor.cablesBeta.wizardBoardsQuestionWorking", {
        found,
        working: working.names.length,
        names: working.names.join(", "),
      })
    : t("smartSurveyor.cablesBeta.wizardBoardsQuestion", { boards: found });

export const karlWizardSteps = (
  t: GapTranslate,
  boardTotal: number,
  working?: { names: string[] },
  heightTotal = 0,
  trayTotal = 0,
  // Absent when the layout detector has not run on this page: there is nothing to confirm.
  wallCounts?: KarlWizardWallCounts,
): KarlWizardStep[] => [
  {
    key: "boards",
    question: boardsQuestion(t, boardTotal, working),
    isDone: (state: KarlWizardState) => state.boardsConfirmed,
    isLocked: () => false,
  },
  {
    key: "heights",
    question: t("smartSurveyor.cablesBeta.wizardHeightsQuestion", { count: heightTotal }),
    isDone: (state: KarlWizardState) => state.heightsConfirmed,
    isLocked: (state: KarlWizardState) => !state.boardsConfirmed,
    lockedKey: "wizardBoardsFirst",
  },
  {
    key: "trays",
    question: t("smartSurveyor.cablesBeta.wizardTraysQuestion", { count: trayTotal }),
    isDone: (state: KarlWizardState) => state.traysConfirmed,
    isLocked: (state: KarlWizardState) => !state.boardsConfirmed || !state.heightsConfirmed,
    lockedKey: "wizardHeightsFirst",
  },
  ...(wallCounts
    ? [
        {
          key: "walls" as const,
          question: t("smartSurveyor.cablesBeta.wizardWallsQuestion", {
            walls: wallCounts.walls,
            doors: wallCounts.doors,
          }),
          isDone: (state: KarlWizardState) => state.wallsConfirmed,
          isLocked: (state: KarlWizardState) =>
            !state.boardsConfirmed || !state.heightsConfirmed || !state.traysConfirmed,
          lockedKey: "wizardTraysBeforeWalls",
        },
      ]
    : []),
];

/** Index of the first step still open, or -1 when the walkthrough is over. */
export const nextOpenStep = (
  steps: KarlWizardStep[],
  from: number,
  state: KarlWizardState,
): number => {
  if (!state.hasLoaded) return Math.max(0, from);
  for (let index = Math.max(0, from); index < steps.length; index += 1) {
    if (!steps[index].isDone(state)) return index;
  }
  return -1;
};
